// src/utils/appearanceSettings.js
//
// Aspectul platformei, ținut pe dispozitiv (localStorage): tema (luminoasă /
// întunecată, pentru toți), iar pentru elevi și culoarea de bază, mărimea
// textului și animațiile. Se aplică pe <body>:
//   .darkmode            tema luminoasă (numele e istoric: „darkmode” = luminos)
//   data-accent="ocean"  paleta culorii de bază (lipsește = cea implicită)
//   data-text-size="lg"  mărimea textului (lipsește = normală)
//   data-motion="reduced" animații reduse
// Stilurile stau în scss/_appearance.scss.

const KEYS = {
   theme: "darkMode", // "enabled" = tema luminoasă (cheia veche, păstrată)
   accent: "appearance.accent",
   textSize: "appearance.textSize",
   motion: "appearance.motion",
};

export const ACCENTS = [
   { key: "lime", label: "Lime", dark: "#e6f680", light: "#584b00" },
   { key: "ocean", label: "Ocean", dark: "#8fd3ff", light: "#00476e" },
   { key: "teal", label: "Turcoaz", dark: "#7fe7dc", light: "#005c56" },
   { key: "coral", label: "Coral", dark: "#ffb48a", light: "#8a3300" },
   { key: "lavender", label: "Lavandă", dark: "#c9b3ff", light: "#4a2a8a" },
   { key: "rose", label: "Roz", dark: "#ffa8cc", light: "#8a1f55" },
];

export const TEXT_SIZES = [
   { key: "sm", label: "Mic" },
   { key: "md", label: "Normal" },
   { key: "lg", label: "Mare" },
];

export const DEFAULTS = {
   theme: "dark",
   accent: "lime",
   textSize: "md",
   motion: "full",
};

const DARK_SURFACE_COLOR = "#1a1a1a";
const LIGHT_SURFACE_COLOR = "#f2f4ef";

function storageGet(key) {
   try {
      return localStorage.getItem(key);
   } catch {
      return null;
   }
}

function storageSet(key, value) {
   try {
      localStorage.setItem(key, value);
   } catch {
      // stocare blocată: setarea ține doar până la reîncărcare
   }
}

const oneOf = (value, list, fallback) =>
   list.some((item) => item.key === value) ? value : fallback;

export function readAppearance() {
   return {
      theme: storageGet(KEYS.theme) === "enabled" ? "light" : "dark",
      accent: oneOf(storageGet(KEYS.accent), ACCENTS, DEFAULTS.accent),
      textSize: oneOf(storageGet(KEYS.textSize), TEXT_SIZES, DEFAULTS.textSize),
      motion: storageGet(KEYS.motion) === "reduced" ? "reduced" : "full",
   };
}

function applySystemBarsColor(color) {
   if (typeof document === "undefined") return;
   document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
      meta.setAttribute("content", color);
   });
   document
      .querySelector('meta[name="msapplication-navbutton-color"]')
      ?.setAttribute("content", color);
   document
      .querySelector('meta[name="nav-color"]')
      ?.setAttribute("content", color);
}

function setBodyData(name, value, defaultValue) {
   const body = document.body;
   if (!body) return;
   if (value === defaultValue) delete body.dataset[name];
   else body.dataset[name] = value;
}

// pentru cine nu e elev, doar tema contează; restul rămâne implicit
let studentExtras = true;

function apply(settings) {
   if (typeof document === "undefined" || !document.body) return;
   const light = settings.theme === "light";
   document.body.classList.toggle("darkmode", light);
   applySystemBarsColor(light ? LIGHT_SURFACE_COLOR : DARK_SURFACE_COLOR);

   const extras = studentExtras ? settings : DEFAULTS;
   setBodyData("accent", extras.accent, DEFAULTS.accent);
   setBodyData("textSize", extras.textSize, DEFAULTS.textSize);
   setBodyData("motion", extras.motion, DEFAULTS.motion);
}

const listeners = new Set();
let current = null;

export function getAppearance() {
   if (!current) current = readAppearance();
   return current;
}

export function subscribeAppearance(listener) {
   listeners.add(listener);
   return () => listeners.delete(listener);
}

/** Schimbă una sau mai multe setări, le salvează și le aplică. */
export function setAppearance(patch) {
   const next = { ...getAppearance(), ...patch };
   current = next;
   storageSet(KEYS.theme, next.theme === "light" ? "enabled" : "disabled");
   storageSet(KEYS.accent, next.accent);
   storageSet(KEYS.textSize, next.textSize);
   storageSet(KEYS.motion, next.motion);
   apply(next);
   listeners.forEach((listener) => listener(next));
}

/** La pornire, înainte de prima randare: fără „clipire” a temei. */
export function applyStoredAppearance() {
   current = readAppearance();
   apply(current);
}

/**
 * Culoarea, mărimea textului și animațiile sunt doar pentru elevi; pentru
 * ceilalți (sau fără cont) rămân cele implicite, chiar dacă dispozitivul a fost
 * folosit de un elev.
 */
export function setStudentAppearance(enabled) {
   if (studentExtras === enabled) return;
   studentExtras = enabled;
   apply(getAppearance());
}

/** Animații reduse: din sistem sau din setări. */
export function prefersReducedMotion() {
   if (typeof window === "undefined") return false;
   if (document.body?.dataset.motion === "reduced") return true;
   return (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
   );
}
